// Cookie / tracker consent — GDPR + ePrivacy (2026-08).
//
// Non-essential trackers (Google Analytics 4, Microsoft Clarity session
// recording, Tawk.to chat) must NOT load until the user gives prior opt-in
// consent. This module is the single source of truth for that decision and
// the ONLY place that injects GA4/Clarity (moved out of index.html so nothing
// fires before consent).

export const CONSENT_KEY = "mototrips_cookie_consent";
export const CONSENT_EVENT = "mototrips-consent-changed";
export const OPEN_CONSENT_EVENT = "mototrips-open-consent";

// Bump when the cookie/privacy policy materially changes → re-prompts users
// and lets the server-side audit log distinguish policy versions.
export const CONSENT_VERSION = "2026-08-01";
const CONSENT_ID_KEY = "mototrips_consent_id";

const GA_ID = "G-KEWCWFKK6W";
const CLARITY_ID = "vdtwypjvb0";

// Stable anonymous id so consent records can be correlated across visits
// (for logged-out users) without using any personal data.
function getAnonId() {
  try {
    let id = localStorage.getItem(CONSENT_ID_KEY);
    if (!id) {
      id =
        (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
        "c_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem(CONSENT_ID_KEY, id);
    }
    return id;
  } catch (e) {
    return null;
  }
}

// Append-only proof of consent stored server-side (GDPR Art. 7). Fire-and-forget.
function recordConsentServer(analytics) {
  try {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) return;
    let lang = "pt";
    try { lang = (localStorage.getItem("i18nextLng") || "pt").slice(0, 2); } catch (e) {}
    fetch(`${base}/api/consent`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      keepalive: true,
      body: JSON.stringify({
        analytics: !!analytics,
        version: CONSENT_VERSION,
        anon_id: getAnonId(),
        lang,
      }),
    }).catch(() => {});
  } catch (e) {
    /* non-fatal */
  }
}

export function getConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    return raw ? JSON.parse(raw) : null; // { analytics: bool, ts }
  } catch (e) {
    return null;
  }
}

export function hasDecided() {
  return getConsent() !== null;
}

export function analyticsAllowed() {
  const c = getConsent();
  return !!(c && c.analytics);
}

export function setConsent(analytics) {
  try {
    localStorage.setItem(
      CONSENT_KEY,
      JSON.stringify({ analytics: !!analytics, ts: Date.now(), version: CONSENT_VERSION })
    );
  } catch (e) {
    /* non-fatal */
  }
  recordConsentServer(analytics); // server-side proof of consent
  if (analytics) loadAnalytics();
  try {
    window.dispatchEvent(new Event(CONSENT_EVENT));
  } catch (e) {
    /* non-fatal */
  }
}

let _analyticsLoaded = false;

// Injects GA4 + Microsoft Clarity — ONLY on production hosts and ONLY once.
// Called exclusively after the user consents (or on load if already consented).
export function loadAnalytics() {
  if (_analyticsLoaded) return;
  const host = window.location.hostname;
  const isProd =
    host === "mototrips.net" ||
    host === "www.mototrips.net" ||
    host.endsWith(".emergent.host");
  _analyticsLoaded = true;
  if (!isProd) return; // never load trackers in dev/preview

  // Microsoft Clarity
  (function (c, l, a, r, i, t, y) {
    c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
    t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i;
    y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
  })(window, document, "clarity", "script", CLARITY_ID);

  // Google Analytics 4
  const ga = document.createElement("script");
  ga.async = true;
  ga.src = "https://www.googletagmanager.com/gtag/js?id=" + GA_ID;
  document.head.appendChild(ga);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", GA_ID);
}

// Run once at app start: if the user already consented in a prior visit,
// load the trackers immediately.
export function initConsent() {
  if (analyticsAllowed()) loadAnalytics();
}
